/**
 * TanStack Router route tree.
 *
 * Auth guard: layoutRoute checks useAuth().user — redirects to /login if null.
 * /login is outside the layout so unauthenticated users see only the login form.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { AppLayout } from '@/components/layout/AppLayout'
import { ErrorFallback } from '@/components/ErrorFallback'
import { LeadsPage } from '@/pages/LeadsPage'
import { ContactPage } from '@/pages/ContactPage'
import { PipelinePage } from '@/pages/PipelinePage'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ClientsPage } from '@/pages/ClientsPage'
import { SelfAgentPage } from '@/pages/SelfAgentPage'
import { BillingPage } from '@/pages/BillingPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { getQueryClient } from '@/queryClient'
import { ME_QUERY_KEY } from '@/hooks/useAuth'
import type { CurrentUser } from '@/types'

// ---- Auth guard helper ----
// Reads the cached /api/me result from QueryClient.

function getUser(): CurrentUser | null {
  const qc = getQueryClient()
  return (qc.getQueryData<CurrentUser | null>(ME_QUERY_KEY)) ?? null
}

// ---- Route definitions ----

const rootRoute = createRootRoute({ component: Outlet })

// Login route — public, outside layout
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    if (getUser()) throw redirect({ to: '/dashboard' })
  },
  component: LoginPage,
})

// Protected layout — guards all nested routes
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  beforeLoad: () => {
    if (!getUser()) throw redirect({ to: '/login' })
  },
  component: AppLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  beforeLoad: () => { throw redirect({ to: '/dashboard' }) },
})

const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: DashboardPage,
})

const leadsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/leads',
  component: LeadsPage,
})

const pipelineRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pipeline',
  component: () => <PipelinePage phase="sales" />,
})

const deliveryRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/delivery',
  component: () => <PipelinePage phase="delivery" />,
})

const supportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/support',
  component: () => <PipelinePage phase="account" />,
})

const clientsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/clients',
  component: ClientsPage,
})

const selfAgentRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/self-agent',
  component: SelfAgentPage,
})

const contactRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/contact/$id',
  component: ContactPage,
})

const billingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/billing',
  component: BillingPage,
})

const reportsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/reports',
  component: ReportsPage,
})

// ---- Router ----

const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    indexRoute,
    dashboardRoute,
    leadsRoute,
    pipelineRoute,
    deliveryRoute,
    supportRoute,
    clientsRoute,
    selfAgentRoute,
    contactRoute,
    billingRoute,
    reportsRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Любая ошибка рендера маршрута → видимая заглушка, а не пустая белая страница.
  defaultErrorComponent: ({ error, reset }) => <ErrorFallback error={error} reset={reset} />,
  defaultNotFoundComponent: () => <ErrorFallback error="Страница не найдена" />,
})

// Type augmentation for TanStack Router
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
